import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';
import { FaSave, FaBuilding } from 'react-icons/fa';

const CompanyInfo = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    company_name: '',
    tagline: '',
    description: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    logo_url: '',
    logo_white_url: '',
    logo_dark_url: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    twitter_url: '',
    youtube_url: ''
  });

  useEffect(() => {
    fetchCompanyInfo();
  }, []);

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/company`);
      if (response.data.success && response.data.data) {
        setCompanyInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching company info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/company`, companyInfo, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Company information updated successfully!');
    } catch (error) {
      console.error('Error updating company info:', error);
      alert('Failed to update company information');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCompanyInfo({
      ...companyInfo,
      [name]: value
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-maroon-700"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <FaBuilding className="text-3xl text-maroon-700 mr-3" />
        <h1 className="text-3xl font-bold text-gray-800">Company Information</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Company Name *</label>
              <input
                type="text"
                name="company_name"
                value={companyInfo.company_name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="PT. ARTHA FINTRUST CONSULTASEAN"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Tagline</label>
              <input
                type="text"
                name="tagline"
                value={companyInfo.tagline}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="Your Trusted Financial Consultant"
              />
            </div>

            <div>
              <label className="block text-gray-700 font-medium mb-2">Description</label>
              <textarea
                name="description"
                value={companyInfo.description}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="Company description..."
              ></textarea>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Contact Information</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Address</label>
              <textarea
                name="address"
                value={companyInfo.address}
                onChange={handleChange}
                rows="2"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="Full company address..."
              ></textarea>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={companyInfo.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="+62 21 xxxx xxxx"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={companyInfo.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="info@company.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Website</label>
                <input
                  type="text"
                  name="website"
                  value={companyInfo.website}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="https://company.com"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Logo URLs */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Logo Images</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Logo URL (Main)</label>
              <input
                type="text"
                name="logo_url"
                value={companyInfo.logo_url}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                placeholder="/assets/logo-maroon.jpg"
              />
              <p className="text-xs text-gray-500 mt-1">Relative path from public folder</p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Logo White (Dark BG)</label>
                <input
                  type="text"
                  name="logo_white_url"
                  value={companyInfo.logo_white_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="/assets/logo-white.jpg"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Logo Dark (Light BG)</label>
                <input
                  type="text"
                  name="logo_dark_url"
                  value={companyInfo.logo_dark_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="/assets/logo-black.jpg"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Social Media Links</h2>
          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Facebook</label>
                <input
                  type="text"
                  name="facebook_url"
                  value={companyInfo.facebook_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="https://facebook.com/company"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Instagram</label>
                <input
                  type="text"
                  name="instagram_url"
                  value={companyInfo.instagram_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="https://instagram.com/company"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">LinkedIn</label>
                <input
                  type="text"
                  name="linkedin_url"
                  value={companyInfo.linkedin_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="https://linkedin.com/company/company"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Twitter</label>
                <input
                  type="text"
                  name="twitter_url"
                  value={companyInfo.twitter_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="https://twitter.com/company"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">YouTube</label>
                <input
                  type="text"
                  name="youtube_url"
                  value={companyInfo.youtube_url}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-maroon-700 focus:border-transparent"
                  placeholder="https://youtube.com/@company"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-maroon-700 hover:bg-maroon-800 text-white px-8 py-3 rounded-lg flex items-center transition-all duration-300 disabled:bg-gray-400"
          >
            <FaSave className="mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CompanyInfo;
